import React, { useEffect, useState } from 'react'
import { useBlocker, useParams } from 'react-router';
import { useDispatch, useSelector } from "react-redux";
import { setEditingApp, setStipulationCategories } from "../../../store/application/actions";
import { AppState } from '../../../store/rootReducer';
import {
  createEmployeeApplicationHistory,
  SET_APP_DIRTY,
  SET_DISABLED_APP_STATUSES,
  SET_SERVER_MESSAGE, setAppLoadedBusinessRulesHaveRun, setShouldOpenGeneratePortalLinkDialog,
  UPDATE_EDITING_APP,
  UPDATE_KEY_FACTOR_VALIDATION_RESULTS,
  getSignedUrls,
} from '../../../store/application/types';
import { useQueryApplication } from '../../../Data/ApplicationData';
import { getCurrentBusinessRuleSet, setBusinessRuleSetId, useBusinessRules, useBusinessRulesEngine, useBusinessRuleSets } from '../../../Data/BusinessRulesData';
import OrderEditorTabs from './OrderEditorTabs';
import deepEqual from 'deep-equal';
import { Constants } from '../../../utils/constants';
import { useEmployeeRolePermissions } from "../../../Data/EmployeeRoleData";
import { usePostedAppLock } from "../../../hooks/usePostedAppLock";
import { updateFinanceQuoteErrorsSuccess, updateLeaseQuoteErrorsSuccess } from "../../../store/Notifications/actions";
import { Typography } from '@mui/material';
import { Window } from '@progress/kendo-react-dialogs';
import { ILoggedInEmployee } from '../../../Interfaces/Admin/ILoggedInEmployee';
import { getFullName, LoadingPanel } from '@amsanalytics/react-shared-library';
import { getApplicant } from '../../../utils/appUtils';
import { setDealerServiceState } from '../../../store/dealerService/actions';
import { useApplicationCustomerStipulations } from '../../../Data/StipulationData';

type OrderEditorProps = {
  OrderId: number;
}
const OrderEditor: React.FC<OrderEditorProps> = (props) => {
  const params: any = useParams();
  const dispatch = useDispatch();
  const applicationState = useSelector((state: AppState) => state.applications);
  const appDirty: boolean = useSelector((state: AppState) => state.applications.appDirty);
  const activeUser: ILoggedInEmployee | undefined = useSelector((state: AppState) => state.applications.loggedInUser?.employee);
  const { data: application } = useQueryApplication(params.id);
  const { data: businessRuleSets } = useBusinessRuleSets(activeUser);
  const { data: businessRules } = useBusinessRules();
  const businessRulesEngine = useBusinessRulesEngine();
  const [appLoaded, setAppLoaded] = useState(false);
  const currentApp = useSelector((state: AppState) => state.applications.editingApp);
  const [canView, canEdit, canAct, canActInStatus] = useEmployeeRolePermissions();
  const { activeUnlock } = usePostedAppLock();
  const shouldOpenGeneratePortalLinkDialogParam: boolean = (params && (params as any).shouldOpenGeneratePortalLinkDialog) ?? false;
  const { data: stipulationData } = useApplicationCustomerStipulations(currentApp ? currentApp.id : Constants.emptyGuid);
  const stipulationCategories = useSelector((state: AppState) => state.applications.stipulationCategories);
  useEffect(() => {
    // sets the global reducer variable on first load
    dispatch(setShouldOpenGeneratePortalLinkDialog(shouldOpenGeneratePortalLinkDialogParam));
  }, []);

  useEffect(() => {
    if (stipulationData?.stipulationCategoryTypes) {
      dispatch(setStipulationCategories(stipulationData?.stipulationCategoryTypes.map((x) => x.stipulationCategories).flat() || []));
    }
  }, [stipulationData, dispatch]);

  const whenToBlock = React.useCallback(() => appDirty && application !== undefined && application.id !== Constants.newApplicationId && canEditInStatus(), [appDirty, application]);
  let blocker = useBlocker(
    ({ currentLocation, nextLocation }) => {
      return currentLocation.pathname !== nextLocation.pathname && whenToBlock();
    }
  );

  const canEditInStatus = (): boolean => {
    return currentApp?.unlockedBySuperUser || !!activeUnlock || canActInStatus(currentApp?.appStatusId || "", "Save");
  }

  useEffect(() => {
    dispatch(setEditingApp(application));
    if (application) {
      dispatch(getSignedUrls(application.id));
    }
    if (application && businessRuleSets) {
      const businessRuleSet = getCurrentBusinessRuleSet(businessRuleSets, application.applicationDate);
      setBusinessRuleSetId(businessRuleSet?.id);
      setAppLoaded(true);
    } else {
      setBusinessRuleSetId();
    }
    return () => {
      setBusinessRuleSetId();
    };
  }, [dispatch, application, businessRuleSets]);

  useEffect(() => {
    if (appLoaded && businessRules && stipulationCategories) {
      const result = businessRulesEngine({ action: 'Load App' }, applicationState);
      if (result?.errors?.length) {
        dispatch({
          type: SET_SERVER_MESSAGE, payload: {
            message: `Error while running business rules after loading application: ${result.errors.reduce((x, y) => x + '\n' + y, '')}`,
            isError: true,
          }
        });
      } else {
        if (result.applicationState?.editingApp && !deepEqual(result.applicationState?.editingApp, applicationState.editingApp)) {
          dispatch({ type: UPDATE_EDITING_APP, payload: result.applicationState.editingApp });
        }
        if (result.applicationState) {
          dispatch({ type: SET_DISABLED_APP_STATUSES, payload: result.applicationState.disabledAppStatuses });
          // update list of keyFactorValidationResults if any are returned
          if (result.applicationState.keyFactorValidationResults?.length > 0) {
            dispatch({ type: UPDATE_KEY_FACTOR_VALIDATION_RESULTS, payload: result.applicationState.keyFactorValidationResults });
          }
        }
        if(result.dealerServiceState) {
          dispatch(setDealerServiceState(result.dealerServiceState) as any);
        }
        if (result.notificationsState) {
          dispatch(updateFinanceQuoteErrorsSuccess(result.notificationsState.financeQuoteErrors) as any);
          dispatch(updateLeaseQuoteErrorsSuccess(result.notificationsState.leaseQuoteErrors ?? []) as any);
        }
        dispatch(setAppLoadedBusinessRulesHaveRun());
      }
    }
  }, [appLoaded, businessRules, stipulationCategories]);

  useEffect(() => {
    return () => {
      dispatch({ type: SET_APP_DIRTY, payload: false });
    };
  }, []);

  // Prevents the user from closing the window or tab
  useEffect(() => {
    function confirmExit(e: any) {
      if (appDirty && currentApp && currentApp.id !== Constants.newApplicationId && canEditInStatus()) {
        e.preventDefault();
        e.returnValue = "Application has unsaved changes, are you sure you want to leave?";
        return e.returnValue;
      }
    }
    window.addEventListener('beforeunload', confirmExit);
    return () => {
      window.removeEventListener('beforeunload', confirmExit);
    };
  }, [appDirty, currentApp]);

  // Whenever the application changes on the
  useEffect(() => {
    if (!currentApp) {
      return;
    }

    dispatch(createEmployeeApplicationHistory({
      applicationId: currentApp.id,
      labelText: `#${currentApp.applicationNo} - ${getFullName(getApplicant(currentApp, Constants.roleApplicantId, true)?.customer, 'Unknown')}`
    }));
  }, [currentApp?.id]);

  if (application && stipulationCategories) {
    return (
      <>
        {blocker.state === "blocked" && (
          <Window width={480} initialHeight={200} themeColor='primary'
            style={{ background: 'white', overflow: 'hidden' }}
            title={<span style={{ color: 'white' }}>Confirm Navigation</span>}
            onClose={() => blocker && blocker.reset && blocker!.reset()}
            modal={true}
          >
            <div style={{ display: 'flex', flexDirection: 'column', flex: 1, height: '100%', alignContent: 'space-between' }}>
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  height: '100%',
                  alignContent: 'flex-start',
                  justifyContent: 'flex-start',
                  overflowY: 'auto',
                }}
              >
                <Typography variant='body1' style={{ fontWeight: '500' }}>You have unsaved changes. Are you sure you want to leave?</Typography>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                <button
                  style={{ flex: 1, marginRight: '10px' }}
                  type="button"
                  className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-base"
                  onClick={() => {
                    blocker && blocker.reset && blocker!.reset();
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  style={{ flex: 1 }}
                  className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-primary"
                  onClick={() => blocker && blocker.proceed && blocker.proceed()}
                >
                  Proceed
                </button>
              </div>
            </div>
          </Window>
        )}
        <OrderEditorTabs />
      </>
    );
  } else {
    return <LoadingPanel label='Loading Application' style={{ position: 'absolute' }} />
  }
}

export default OrderEditor;
